import {type ReactElement, useEffect, useMemo, useState} from 'react'
import {yupResolver} from '@hookform/resolvers/yup'
import {useForm} from 'react-hook-form'
import {useEventCallback} from 'usehooks-ts'

import {APP_API_URL, DEV_OVERLAY} from '#/lib/constants'
import {makeProfileLink} from '#/lib/routes/links'
import {type SignUpFormData, signUpSchema} from '#/lib/schemas/sign-up'
import {logEvent} from '#/lib/statsig/statsig'
import {cleanError} from '#/lib/strings/errors'
import {isWeb} from '#/platform/detection'
import * as persisted from '#/state/persisted'
import {useSetHasCheckedForStarterPack} from '#/state/preferences/used-starter-packs'
import {checkHandleAvailability} from '#/state/queries/handle-availability.ts'
import {useAgent, useSession, useSessionApi} from '#/state/session'
import {useIsMobileWeb} from '#/alf'
import {Loader} from '#/components/Loader'
import {useGeolocationServiceResponse} from '#/geolocation/service'
import {getHandleParts, usePdsDomain} from '#/wsocial/utils/pdsUtils.ts'
import {DevStepPicker} from './DevStepPicker'
import {
  // IllustrationEurope,  // used by commented info1
  // IllustrationPrivacy, // used by commented info2
  IllustrationVerify,
} from './illustrations'
import {InfoSlide} from './InfoSlide'
import {PageShell} from './primitives'
import {StepAccount1, StepAccount2} from './StepAccount'
// import {StepAgeVerify} from './StepAgeVerify'  // replaced by installWid geo variant
import {StepCongrats} from './StepCongrats'
import {StepCreateWid} from './StepCreateWid'
import {StepInstallWid} from './StepInstallWid'
// import {StepInterests} from './StepInterests'  // interests moved to settings
import {StepInvite} from './StepInvite'
import {StepScanSuccessful} from './StepScanSuccessful'
import {StepVerifying} from './StepVerifying'
import {StepVerifyingMobile} from './StepVerifyingMobile'
import {StepWidSetup} from './StepWidSetup'
import {StepWidSetupMobile} from './StepWidSetupMobile'
import {type SignUpForm, type Step} from './types'
import {useFinishOnboarding} from './useFinishOnboarding'
import {useWidVerificationAutoAdvance} from './useWidVerificationAutoAdvance'

export type {SignUpForm}

const DEV_STEPS: Step[] = [
  'invite',
  'account1',
  'account2',
  'info3',
  'installWid',
  'widSetup',
  'createWid',
  'info5',
  'verifying',
  'congrats',
  'finishing',
]

interface Prefill {
  email?: string
  handle?: string
  inviteCode?: string
}

export function SignUpWizard({
  onComplete,
  prefill,
}: {
  onComplete: () => void
  prefill?: Prefill
}) {
  const agent = useAgent()
  const {createAccount} = useSessionApi()
  const setHasCheckedForStarterPack = useSetHasCheckedForStarterPack()
  const onFinishOnBoarding = useFinishOnboarding()
  const isMobileWeb = useIsMobileWeb()

  const pdsDomain = usePdsDomain()

  const {hasSession, currentAccount} = useSession()

  const hasPrefill = !!(
    prefill?.email &&
    prefill?.handle &&
    prefill?.inviteCode
  )

  const [step, setStep] = useState<Step>(() => {
    if (hasSession) {
      // If account creation is still in progress (pendingOnboarding flag),
      // show a blank loading screen until onFinishOnBoarding resolves and
      // goAfterOnboarding() sets the correct step. Without this the wizard
      // briefly flashes 'installWid' before the right step appears.
      if (persisted.get('pendingOnboarding')) return 'finishing'
      return 'installWid' // placeholder — shouldRedirectToTrends handles this
    }
    return hasPrefill ? 'account1' : 'invite'
  })

  // Logged-in users go to Trends. Exception: pendingOnboarding is set only
  // during account creation — we must let onFinishOnBoarding complete first.
  const [shouldRedirectToTrends] = useState(
    () => hasSession && !persisted.get('pendingOnboarding'),
  )

  const [error, setError] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const geolocationServiceResponse = useGeolocationServiceResponse()
  const geoRestricted = useMemo(
    () => geolocationServiceResponse?.requiredValidation ?? false,
    [geolocationServiceResponse],
  )

  const _onSkip = useEventCallback(() => {
    go('widSetup')
  })

  const form = useForm<SignUpFormData>({
    resolver: yupResolver(signUpSchema),
    defaultValues: {
      inviteCode: prefill?.inviteCode ?? '',
      name: '',
      email: prefill?.email ?? '',
      password: '',
      isAdult: false,
      agreedToTerms: false,
      handle: prefill?.handle ?? '',
      selectedInterests: [],
    },
    mode: 'onChange',
  })

  const go = useEventCallback((next: Step) => {
    setError('')
    setStep(next)
  })

  // useEventCallback always reads latest geoRestricted value, avoiding stale closure.
  const goAfterOnboarding = useEventCallback(() => {
    go(geoRestricted ? 'installWid' : 'info3')
  })

  // Redirect returning logged-in users straight to Trends.
  useEffect(() => {
    if (shouldRedirectToTrends) {
      window.location.replace('/search')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Trigger onFinishOnBoarding only after React re-renders with the new
  // authenticated agent. useSyncExternalStore may fire currentAccount update
  // synchronously inside createAccount(), before the Promise returns, and the
  // component may remount when hasSession changes — so we use persisted storage
  // (survives remounts) instead of a ref to track the pending flag.
  useEffect(() => {
    if (!persisted.get('pendingOnboarding') || !currentAccount?.did) return
    void persisted.write('pendingOnboarding', undefined)
    void onFinishOnBoarding()
      .then(() => {
        setIsProcessing(false)
        goAfterOnboarding()
      })
      .catch(err => {
        setError(cleanError(err))
        setIsProcessing(false)
      })
    // go/setError/setIsProcessing/onFinishOnBoarding are stable refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccount?.did])

  const checkUserHandle = useEventCallback(
    async ({
      localPart,
      fullHandle,
    }: {
      localPart: string
      fullHandle: string
    }) => {
      if (fullHandle) {
        const {available} = await checkHandleAvailability(fullHandle)
        if (!available) {
          setError(
            `The username "${localPart}" is already taken. Please choose a different one.`,
          )
        }
        return available
      }
      return false
    },
  )

  const handleCreateAccount = useEventCallback(async () => {
    if (isProcessing) return
    setError('')
    setIsProcessing(true)
    try {
      const values = form.getValues()
      const {localPart, fullHandle} = getHandleParts(pdsDomain, values.handle)

      const available = await checkUserHandle({localPart, fullHandle})
      if (!available) {
        setIsProcessing(false)
        return
      }

      void persisted.write('lastSelectedHomeFeed', undefined)
      if (isWeb) sessionStorage.removeItem('lastSelectedHomeFeed')
      // Set BEFORE createAccount() — useSyncExternalStore may render
      // synchronously inside createAccount(), and the component may remount
      // when hasSession changes, so we use persisted storage (not a ref).
      void persisted.write('pendingOnboarding', true)
      await createAccount(
        {
          service: APP_API_URL,
          email: values.email,
          password: values.password,
          handle: fullHandle,
          displayName: values.name,
          birthDate: new Date(
            new Date().getFullYear() - 18,
            new Date().getMonth(),
            new Date().getDate(),
          ),
          inviteCode: values.inviteCode || undefined,
        },
        {signupDuration: 0, fieldErrorsTotal: 0, backgroundCount: 0},
      )
      setHasCheckedForStarterPack(true)
      // isProcessing stays true; the useEffect handles setIsProcessing(false).
    } catch (err: unknown) {
      void persisted.write('pendingOnboarding', undefined)
      setError(cleanError(err))
      setIsProcessing(false)
    }
  })

  const _handleSaveInterests = useEventCallback(async () => {
    setIsProcessing(true)
    const selectedInterests = form.getValues('selectedInterests')
    try {
      if (selectedInterests.length > 0) {
        await agent.setInterestsPref({tags: selectedInterests})
      }
      logEvent('onboarding:interests:nextPressed', {
        selectedInterests,
        selectedInterestsLength: selectedInterests.length,
      })
    } catch {
      // non-fatal
    } finally {
      setIsProcessing(false)
    }
    go('installWid')
  })

  const handleStartVerifying = useEventCallback(() => {
    go('verifying')
  })

  useWidVerificationAutoAdvance({
    step,
    onScanned: () => go('verifying'),
    onVerified: () => go('congrats'),
  })

  const exitCongrats = useEventCallback(() => {
    onComplete()
  })

  const exitCongratsToProfile = useEventCallback(() => {
    if (currentAccount) {
      window.location.replace(makeProfileLink(currentAccount))
    } else {
      onComplete()
    }
  })

  if (shouldRedirectToTrends) {
    return null
  }

  const content = ((): ReactElement | null => {
    switch (step) {
      // Figma: Onboarding — Invitation code. Desktop 2393:17182 / Mobile 3253:24091
      case 'invite': {
        return <StepInvite form={form} onNext={() => go('account1')} />
      }
      // Figma: Onboarding — Create account — Step 1 (name, email, password, checkboxes). Desktop 2336:12427 / Mobile 3253:23945
      case 'account1': {
        return (
          <StepAccount1
            form={form}
            onBack={hasPrefill ? undefined : () => go('invite')}
            onNext={() => go('account2')}
            error={error}
            hasPrefilledEmail={!!prefill?.email}
          />
        )
      }
      // Figma: Onboarding — Create account — Step 2 (username, submit). Desktop 2379:16426 / Mobile 3253:23971
      case 'account2': {
        return (
          <StepAccount2
            form={form}
            onBack={() => go('account1')}
            onSubmit={() => void handleCreateAccount()}
            error={error}
            isProcessing={isProcessing}
            pdsDomain={pdsDomain}
          />
        )
      }
      // case 'interests': {
      //   return (
      //     <StepInterests
      //       form={form}
      //       onNext={() => void handleSaveInterests()}
      //       saving={isProcessing}
      //     />
      //   )
      // }
      // case 'info1': {
      //   return (
      //     <InfoSlide
      //       illustration={<IllustrationEurope />}
      //       title="Made in Europe — for the world."
      //       body="W is a public microblog. Data you publish on W is hosted in Europe and is publicly accessible worldwide via the open AT-Protocol."
      //       onBack={() => go('interests')}
      //       onNext={() => go('info2')}
      //     />
      //   )
      // }
      // case 'info2': {
      //   return (
      //     <InfoSlide
      //       illustration={<IllustrationPrivacy />}
      //       title="Real conversations"
      //       body="We help you discover trending content across the world, with a focus on posts from verified users with authentic identities and trusted sources."
      //       onBack={() => go('info1')}
      //       onNext={() => go('info3')}
      //     />
      //   )
      // }
      /*
       * Figma: Verification — Step 1a (users coming from onboarding)
       * Desktop 4609:16212 / Mobile 5410:6251
       * Shown only to non-geo-restricted users after account creation.
       */
      case 'info3': {
        return (
          <InfoSlide
            illustration={<IllustrationVerify />}
            title="Your account is ready"
            body="Explore W now, or verify your account with an ID to post and interact. Verified accounts get a badge, and verifying your name adds visibility and trust."
            onSkip={onComplete}
            skipLabel="Explore W without verifying"
            onNext={() => go('installWid')}
            nextLabel="Verify with ID"
          />
        )
      }
      /*
       * Figma: Verification — Step 2 (install W Identity app)
       * Desktop 4609:16253 / Mobile 3253:24059
       * Entry point for geo-restricted users (skips info3).
       */
      case 'installWid': {
        return (
          <StepInstallWid
            geoRestricted={geoRestricted}
            onBack={() => go(geoRestricted ? 'account2' : 'info3')}
            onSkip={geoRestricted ? undefined : onComplete}
            onNext={() => go('widSetup')}
          />
        )
      }
      /*
       * Figma: Verification — Step 4a (QR code scanned successfully)
       * Desktop 4834:4352 / Mobile 4849:4760
       */
      case 'info5': {
        return (
          <StepScanSuccessful
            onBack={() => go('widSetup')}
            onSkip={geoRestricted ? undefined : onComplete}
            onNext={handleStartVerifying}
          />
        )
      }
      /*
       * Figma: Verification — Step 3 (scan QR code with W Identity)
       * Desktop 4609:16163 / Mobile: StepWidSetupMobile (mobile-only deep link variant)
       */
      case 'widSetup': {
        if (isMobileWeb) {
          return (
            <StepWidSetupMobile
              onBack={() => go('installWid')}
              onSkip={geoRestricted ? undefined : onComplete}
            />
          )
        }
        return (
          <StepWidSetup
            onBack={() => go('installWid')}
            onSkip={geoRestricted ? undefined : onComplete}
          />
        )
      }
      // Figma: Verification — Step 3 variant (create new WID credential)
      case 'createWid': {
        return (
          <StepCreateWid
            onBack={() => go('widSetup')}
            onSkip={geoRestricted ? undefined : onComplete}
            onNext={() => go('info5')}
          />
        )
      }
      /*
       * Figma: Verification — Step 4b (verifying identity, waiting for result)
       * Desktop 4978:6872 / Mobile 4978:6938
       */
      case 'verifying': {
        if (isMobileWeb) {
          return (
            <StepVerifyingMobile
              onSkip={onComplete}
              allowSkip={!geoRestricted}
            />
          )
        }
        return <StepVerifying onSkip={onComplete} allowSkip={!geoRestricted} />
      }
      // case 'ageVerify': replaced by installWid geo variant

      /*
       * Figma: Verification — Step 5 (verification complete)
       * Desktop 4609:16180 / Mobile 3308:26216
       */
      case 'congrats': {
        return (
          <StepCongrats
            // onBack={() => go('verifying')}
            onSkip={exitCongrats}
            onNext={exitCongratsToProfile}
          />
        )
      }
      // Blank loading screen shown while onFinishOnBoarding resolves after account creation
      case 'finishing': {
        return (
          <PageShell>
            <Loader size="xl" />
          </PageShell>
        )
      }
      default: {
        return null
      }
    }
  })()

  return (
    <>
      {DEV_OVERLAY && (
        <DevStepPicker steps={DEV_STEPS} step={step} go={s => go(s as Step)} />
      )}
      {content}
    </>
  )
}
